import React, { useState, useEffect } from 'react';
import { Shield, Cookie, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show immediately for EWR compliance
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Cookie Banner - Full screen on mobile, centered modal on desktop */}
      <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-0 md:p-4">
        <div className="bg-white w-full md:max-w-2xl md:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 md:p-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Datenschutz-Einstellungen
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Wir respektieren Ihre Privatsphäre
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              Wir verwenden Cookies und ähnliche Technologien auf unserer Website. 
              Einige von ihnen sind essenziell, während andere uns helfen, diese Website 
              und Ihre Erfahrung zu verbessern.
            </p>

            {/* Cookie Categories */}
            <div className="space-y-4 mb-6">
              {/* Necessary */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Notwendige Cookies</h3>
                      <p className="text-sm text-gray-500">Immer aktiv</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    Erforderlich
                  </span>
                </div>
                {showDetails && (
                  <p className="text-sm text-gray-600 mt-3 pl-13">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich 
                    und können nicht deaktiviert werden. Sie speichern z.B. Ihre Cookie-Präferenzen.
                  </p>
                )}
              </div>

              {/* Analytics */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Cookie className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Analyse-Cookies</h3>
                      <p className="text-sm text-gray-500">Optional</p>
                    </div>
                  </div>
                </div>
                {showDetails && (
                  <p className="text-sm text-gray-600 mt-3 pl-13">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                    indem Informationen anonym gesammelt und analysiert werden.
                  </p>
                )}
              </div>
            </div>

            {/* Toggle Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-6"
            >
              <Settings className="w-4 h-4" />
              {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
            </button>

            {/* Links */}
            <p className="text-sm text-gray-500 mb-6">
              Weitere Informationen finden Sie in unserer{' '}
              <a href="/datenschutz" className="text-blue-600 hover:text-blue-700 underline">
                Datenschutzerklärung
              </a>
              {' '}und im{' '}
              <a href="/impressum" className="text-blue-600 hover:text-blue-700 underline">
                Impressum
              </a>.
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={acceptAll}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-base py-6"
                size="lg"
              >
                Alle akzeptieren
              </Button>
              <Button
                onClick={acceptNecessary}
                variant="outline"
                className="flex-1 border-gray-300 text-base py-6"
                size="lg"
              >
                Nur notwendige
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
